/**
 * OfflineQueue — Persistent localStorage-backed queue for failed critical writes.
 * Survives page reloads, browser crashes, backend outages.
 * Each operation stores full payload so it can be retried verbatim when backend is back.
 *
 * Usage:
 *   offlineQueue.enqueue({ method, url, data, label })
 *   offlineQueue.getAll()           // read all pending
 *   offlineQueue.remove(id)         // remove one after successful flush
 *   offlineQueue.size()             // count
 *   offlineQueue.subscribe(cb)      // listen to queue changes
 */

const STORAGE_KEY = "edmundo_offline_queue_v1";
const MAX_ITEMS = 500;           // Límite duro de cola offline
const MAX_AGE_DAYS = 7;          // Items más viejos que esto se descartan (evita leaks)
const MAX_ATTEMPTS = 10;         // Items con muchos reintentos se descartan

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

/** Purga items viejos o con demasiados reintentos. Retorna lista saneada. */
const prune = (items) => {
  const cutoff = Date.now() - MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
  let pruned = items.filter((it) => {
    try {
      const t = new Date(it.createdAt).getTime();
      if (!Number.isFinite(t)) return false;  // fecha corrupta → descartar
      if (t < cutoff) return false;            // demasiado viejo
      if ((it.attempts || 0) >= MAX_ATTEMPTS) return false;  // intentos agotados
      return true;
    } catch {
      return false;
    }
  });
  // Hard cap — conservar los MAX_ITEMS más recientes
  if (pruned.length > MAX_ITEMS) {
    pruned = pruned.slice(-MAX_ITEMS);
  }
  return pruned;
};

const save = (items) => {
  try {
    const safe = prune(items);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(safe));
    listeners.forEach((cb) => {
      try { cb(safe); } catch (_e) { /* swallow */ }
    });
  } catch (e) {
    // QuotaExceededError: localStorage lleno. Limpiar para evitar bloqueo total.
    console.error("[offlineQueue] localStorage save failed, clearing queue:", e);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_) { /* swallow */ }
  }
};

const listeners = new Set();

const genId = () => {
  const t = Date.now().toString(36);
  const r = Math.random().toString(36).slice(2, 10);
  return `op_${t}_${r}`;
};

export const offlineQueue = {
  enqueue({ method, url, data, label, headers }) {
    const items = load();
    const item = {
      id: genId(),
      method: (method || "post").toLowerCase(),
      url,
      data: data ?? null,
      headers: headers || null,
      label: label || `${(method || "POST").toUpperCase()} ${url}`,
      createdAt: new Date().toISOString(),
      attempts: 0,
      lastError: null,
    };
    items.push(item);
    save(items);
    return item;
  },

  getAll() {
    return load();
  },

  size() {
    return load().length;
  },

  remove(id) {
    const items = load().filter((i) => i.id !== id);
    save(items);
  },

  updateAttempt(id, errorMsg) {
    const items = load();
    const idx = items.findIndex((i) => i.id === id);
    if (idx >= 0) {
      items[idx].attempts += 1;
      items[idx].lastError = errorMsg || null;
      save(items);
    }
  },

  clear() {
    save([]);
  },

  subscribe(cb) {
    listeners.add(cb);
    return () => listeners.delete(cb);
  },
};

// Cross-tab sync: if user has multiple tabs open, keep queue in sync
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) {
      const items = load();
      listeners.forEach((cb) => {
        try { cb(items); } catch (_e) { /* swallow */ }
      });
    }
  });
}

export default offlineQueue;
